/**
 * REL v1 lexer.
 *
 * Normative: docs/05-logic-and-expressions.md §2 (lexical structure), §3 (EBNF),
 * §4 (references), §13 (limits and the rejected `__proto__`/`constructor`/`prototype`
 * names). The lexer is a single forward pass; every token carries its source
 * offsets so `RASD_EXPR_PARSE` can point at the exact character.
 *
 * Two rules make this lexer unusual and are worth stating up front:
 *
 * 1. **Strings have no escape sequences** (05 §2). A backslash is an ordinary
 *    character, so XLSForm regex patterns such as `'^\d{9}$'` survive import
 *    unchanged. To embed a quote, authors use the other delimiter.
 * 2. **Hyphenated identifiers exist only in call position** (spine §5). We lex a
 *    hyphen run speculatively and backtrack unless it is immediately followed by
 *    `(`, so `${a}-1` and `x - y` still lex as subtraction.
 */
import { RasdError } from '../errors.js';
/**
 * Source-length guard (05 §2 "Limits": source ≤ 8 KiB). Counted in UTF-16 code
 * units rather than UTF-8 bytes so the check needs no `TextEncoder` (which is
 * not guaranteed on older Hermes). For ASCII sources the two are identical; for
 * non-ASCII sources this is the more permissive of the two, which is fine — the
 * limit is a denial-of-service guard, not a semantic rule.
 */
export const MAX_SOURCE_LENGTH = 8192;
export const KEYWORDS = ['and', 'or', 'not', 'div', 'mod', 'true', 'false', 'null'];
const KEYWORD_SET = new Set(KEYWORDS);
/** Multi-character punctuators, matched before their single-character prefixes. */
const TWO_CHAR = new Set(['<=', '>=', '!=', '==', '&&', '||']);
const SINGLE_CHAR = '()[],?:.+-*/%=<>!';
/**
 * Property names that must never reach a data-tree lookup (05 §13). Rejected in
 * `${…}` path segments and in postfix `.name` access at parse time, so no
 * downstream consumer has to re-check.
 */
const FORBIDDEN_NAMES = new Set(['__proto__', 'constructor', 'prototype']);
/** Builds the one error every static REL failure uses (05 §12, 17 §2.4). */
export function exprParseError(source, offset, message, expected) {
    const details = { source, offset };
    if (expected !== undefined)
        details['expected'] = expected;
    const where = expected === undefined ? '' : `; expected ${expected}`;
    return new RasdError('RASD_EXPR_PARSE', `${message} at offset ${offset}${where}`, { details });
}
export function assertSafeName(name, source, offset) {
    if (FORBIDDEN_NAMES.has(name)) {
        throw exprParseError(source, offset, `the name '${name}' is not allowed in a path`);
    }
}
/** Human-readable token label used in parse-error messages. */
export function describeToken(t) {
    switch (t.type) {
        case 'num':
            return `number '${t.num}'`;
        case 'str':
            return 'string literal';
        case 'ident':
            return `identifier '${t.name}'`;
        case 'kw':
            return `keyword '${t.name}'`;
        case 'ref':
            return 'reference';
        case 'punct':
            return `'${t.value}'`;
        case 'eof':
            return 'end of expression';
    }
}
function isDigit(ch) {
    return ch >= '0' && ch <= '9';
}
/** Identifiers are ASCII only (05 §2): `[A-Za-z_][A-Za-z0-9_]*`. */
function isIdentStart(ch) {
    return (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_';
}
function isIdentPart(ch) {
    return isIdentStart(ch) || isDigit(ch);
}
function isSpace(ch) {
    return ch === ' ' || ch === '\t' || ch === '\r' || ch === '\n';
}
/**
 * Reads `${ RefPath }` starting at `start` (which points at `$`).
 *
 * The whole reference — hops, path segments and `[n]` / `[]` brackets — is one
 * token: references have their own micro-grammar (05 §4) that shares nothing
 * with the expression grammar, and keeping it here means the parser never sees
 * a `.` or `[` that is not an expression operator.
 */
function readReference(src, start) {
    const bodyStart = start + 2;
    const close = src.indexOf('}', bodyStart);
    if (close === -1)
        throw exprParseError(src, start, 'unterminated ${…} reference', "'}'");
    const body = src.slice(bodyStart, close);
    if (body.length === 0)
        throw exprParseError(src, bodyStart, 'empty ${} reference', 'a name');
    // Whitespace inside the braces is forbidden (05 §2) — flag it where it occurs
    // rather than reporting a confusing "unexpected character" further along.
    for (let k = 0; k < body.length; k++) {
        const ch = body.charAt(k);
        if (isSpace(ch) || ch === '{') {
            throw exprParseError(src, bodyStart + k, 'whitespace or "{" inside a ${…} reference');
        }
    }
    let i = 0;
    let up = 0;
    let root = false;
    if (body.charAt(0) === '/') {
        root = true;
        i = 1;
    }
    else {
        while (body.startsWith('../', i)) {
            up++;
            i += 3;
        }
        // `${..}` = the enclosing instance object; `${../..}` = two levels out.
        // (The EBNF spells only the one-level form; accepting the repeated form
        // keeps `printExpression` round-trippable for any `up` with an empty path.)
        if (body.slice(i) === '..') {
            return { ref: { path: [], up: up + 1, root: false, meta: false }, end: close + 1 };
        }
    }
    const path = [];
    for (;;) {
        const nameStart = i;
        if (!isIdentStart(body.charAt(i))) {
            throw exprParseError(src, bodyStart + i, 'invalid ${…} reference path', 'a name');
        }
        i++;
        while (isIdentPart(body.charAt(i)))
            i++;
        const name = body.slice(nameStart, i);
        assertSafeName(name, src, bodyStart + nameStart);
        let index;
        if (body.charAt(i) === '[') {
            const bracket = i;
            i++;
            const digitsStart = i;
            while (isDigit(body.charAt(i)))
                i++;
            const digits = body.slice(digitsStart, i);
            if (body.charAt(i) !== ']') {
                throw exprParseError(src, bodyStart + i, 'invalid repeat index', "']' or a literal integer");
            }
            i++;
            if (digits === '') {
                index = null; // `[]` — every active instance
            }
            else {
                const n = Number(digits);
                if (!Number.isSafeInteger(n)) {
                    throw exprParseError(src, bodyStart + bracket, 'repeat index is out of range');
                }
                index = n; // 1-based (05 §4); out-of-range indices are a runtime null, not a parse error
            }
        }
        path.push(index === undefined ? { name } : { name, index });
        if (body.charAt(i) === '.') {
            i++;
            continue;
        }
        break;
    }
    if (i !== body.length) {
        throw exprParseError(src, bodyStart + i, 'invalid ${…} reference path');
    }
    // `${meta.x…}` is preload metadata, not a form element (spine §4.4). The
    // stored `path` drops the `meta` head so evaluators index `ctx.meta` directly.
    const head = path[0];
    let meta = false;
    if (!root && up === 0 && path.length >= 2 && head !== undefined && head.name === 'meta' && head.index === undefined) {
        meta = true;
        path.shift();
        for (const seg of path) {
            if (seg.index !== undefined) {
                throw exprParseError(src, bodyStart, 'meta references cannot be indexed');
            }
        }
    }
    return { ref: { path, up, root, meta }, end: close + 1 };
}
/** Tokenises a REL source string. Throws `RASD_EXPR_PARSE` on any lexical error. */
export function tokenize(src) {
    if (src.length > MAX_SOURCE_LENGTH) {
        throw exprParseError(src, MAX_SOURCE_LENGTH, `expression exceeds ${MAX_SOURCE_LENGTH} characters`);
    }
    const out = [];
    let i = 0;
    while (i < src.length) {
        const c = src.charAt(i);
        if (isSpace(c)) {
            i++;
            continue;
        }
        const o = i;
        // Number — `DIGITS ('.' DIGITS)?` or `'.' DIGITS`. No exponent, no sign.
        if (isDigit(c) || (c === '.' && isDigit(src.charAt(i + 1)))) {
            while (isDigit(src.charAt(i)))
                i++;
            if (src.charAt(i) === '.' && isDigit(src.charAt(i + 1))) {
                i++;
                while (isDigit(src.charAt(i)))
                    i++;
            }
            out.push({ type: 'num', o, end: i, num: Number(src.slice(o, i)) });
            continue;
        }
        if (c === "'" || c === '"') {
            const close = src.indexOf(c, i + 1);
            if (close === -1) {
                throw exprParseError(src, o, 'unterminated string literal', `a closing ${c}`);
            }
            out.push({ type: 'str', o, end: close + 1, str: src.slice(i + 1, close) });
            i = close + 1;
            continue;
        }
        if (c === '$' && src.charAt(i + 1) === '{') {
            const r = readReference(src, i);
            out.push({ type: 'ref', o, end: r.end, ref: r.ref });
            i = r.end;
            continue;
        }
        if (isIdentStart(c)) {
            i++;
            while (isIdentPart(src.charAt(i)))
                i++;
            const plainEnd = i;
            // Speculative hyphen run: `count-selected(` is one name, `x-y` is `x - y`.
            let j = i;
            while (src.charAt(j) === '-' && isIdentStart(src.charAt(j + 1))) {
                j++;
                while (isIdentPart(src.charAt(j)))
                    j++;
            }
            if (j > plainEnd && src.charAt(j) === '(')
                i = j;
            const name = src.slice(o, i);
            if (i === plainEnd && KEYWORD_SET.has(name)) {
                out.push({ type: 'kw', o, end: i, name: name });
            }
            else {
                out.push({ type: 'ident', o, end: i, name, hyphenated: i !== plainEnd });
            }
            continue;
        }
        const two = src.slice(i, i + 2);
        if (TWO_CHAR.has(two)) {
            out.push({ type: 'punct', o, end: i + 2, value: two });
            i += 2;
            continue;
        }
        if (SINGLE_CHAR.includes(c)) {
            out.push({ type: 'punct', o, end: i + 1, value: c });
            i++;
            continue;
        }
        throw exprParseError(src, o, `unexpected character ${JSON.stringify(c)}`);
    }
    out.push({ type: 'eof', o: src.length, end: src.length });
    return out;
}
//# sourceMappingURL=lexer.js.map